import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Shield, ShieldAlert, ShieldCheck, RefreshCw, Info, PiggyBank } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const compact = (n) => new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(n || 0);

function GaugeArc({ health, pctFunded }) {
  const clamped = Math.max(0, Math.min(1, pctFunded));
  const angle = -90 + clamped * 180; // -90deg (empty) → +90deg (full)
  const meta = {
    healthy:     { color: "#10b981", Icon: ShieldCheck,  label: "Fully funded",  tone: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
    partial:     { color: "#f59e0b", Icon: Shield,       label: "Partially funded", tone: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
    underfunded: { color: "#ef4444", Icon: ShieldAlert,  label: "Underfunded",   tone: "text-red-300 bg-red-500/10 border-red-500/30" },
  }[health] || { color: "#71717a", Icon: Shield, label: "Unknown", tone: "border-white/10 text-zinc-300" };
  const { Icon } = meta;
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-24 h-14 shrink-0" data-testid="reserve-gauge">
        <div className="absolute inset-0 rounded-t-full border-8 border-white/5 border-b-transparent" />
        <div
          className="absolute inset-0 rounded-t-full border-8 border-transparent border-b-transparent"
          style={{
            borderTopColor: meta.color,
            borderRightColor: clamped > 0.5 ? meta.color : "transparent",
            borderLeftColor: clamped > 0 ? meta.color : "transparent",
            transform: `rotate(${angle}deg)`,
            transformOrigin: "50% 100%",
            transition: "transform 700ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
      </div>
      <div>
        <Badge variant="outline" className={"gap-1 " + meta.tone} data-testid={`reserve-tier-${health}`}>
          <Icon className="h-3 w-3" /> {meta.label}
        </Badge>
        <div className="text-[11px] text-zinc-500 mt-1 tabular-nums">
          {(clamped * 100).toFixed(0)}% of trailing income
        </div>
      </div>
    </div>
  );
}

function TooltipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-white/10 bg-[#0a0a0b]/95 backdrop-blur px-3 py-2 text-xs shadow-lg">
      <div className="text-zinc-300 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-zinc-400">{p.name}:</span>
          <span className="text-white tabular-nums">{money(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export function ReserveForecastCard({ scope = "all" }) {
  const [years, setYears] = useState(5);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/finance/reserve-forecast", {
        params: { property_id: scope, years },
      });
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load reserve forecast");
    } finally {
      setLoading(false);
    }
  }, [scope, years]);

  useEffect(() => { load(); }, [load]);

  const endBalance = data?.end_balance || 0;
  const startBalance = data?.starting_reserve || 0;
  const trailingDues = data?.annual_dues || 0;

  const summaryLine = useMemo(() => {
    if (!data) return null;
    const change = endBalance - startBalance;
    const positive = change >= 0;
    return (
      <span>
        Reserve projected to move from <b className="text-white tabular-nums">{money(startBalance)}</b>{" "}
        to <b className={"tabular-nums " + (positive ? "text-emerald-300" : "text-red-300")}>{money(endBalance)}</b>{" "}
        in <b>{years} years</b> · assumes +{Math.round((data.assumptions?.dues_growth_pct || 0) * 100)}% dues,{" "}
        +{Math.round((data.assumptions?.expense_inflation_pct || 0) * 100)}% expense inflation
      </span>
    );
  }, [data, endBalance, startBalance, years]);

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="reserve-forecast-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="overline">Reserves</div>
          <div className="text-base font-semibold font-display mt-0.5 inline-flex items-center gap-2">
            <PiggyBank className="h-4 w-4 text-emerald-400" /> Reserve Fund Forecast
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            5-year projection driven by trailing dues income minus operating expenses.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(years)} onValueChange={(v) => setYears(Number(v))}>
            <SelectTrigger data-testid="reserve-years" className="w-28 bg-white/[0.04] border-white/10 text-white h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
              <SelectItem value="3">3 years</SelectItem>
              <SelectItem value="5">5 years</SelectItem>
              <SelectItem value="10">10 years</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-8" data-testid="reserve-refresh">
            <RefreshCw className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")} />
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.02] p-3 mb-3">
            <GaugeArc health={data.health} pctFunded={data.pct_funded} />
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Ending balance</div>
              <div className="text-2xl font-semibold font-display text-white tabular-nums" data-testid="reserve-end-balance">
                {money(endBalance)}
              </div>
            </div>
          </div>

          {data.starting_reserve_estimated && (
            <div className="mb-3 rounded-md border border-amber-500/20 bg-amber-500/[0.05] px-3 py-2 text-[11px] text-amber-200 inline-flex items-start gap-2" data-testid="reserve-estimated-warning">
              <Info className="h-3 w-3 mt-0.5 shrink-0" />
              <span>
                Starting reserve is estimated from trailing dues since no <code>reserve_fund</code> is set on this
                community&apos;s property record. Set it in Admin → Settings for a precise projection.
              </span>
            </div>
          )}

          <div style={{ width: "100%", height: 240, minHeight: 240 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={240}>
              <ComposedChart data={data.projection} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="reserveG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="label" stroke="#71717a" tick={{ fontSize: 11 }} />
                <YAxis stroke="#71717a" tick={{ fontSize: 11 }} tickFormatter={compact} />
                <Tooltip content={<TooltipBox />} />
                <Legend wrapperStyle={{ fontSize: 12, color: "#a1a1aa" }} />
                <Area type="monotone" dataKey="balance" stroke="#10b981" fill="url(#reserveG)" strokeWidth={2} name="Reserve balance" />
                <Line type="monotone" dataKey="dues" stroke="#f97316" strokeWidth={1.5} dot={false} name="Annual dues" />
                <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={1.5} dot={false} name="Annual expenses" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-3 text-[11px] text-zinc-500">{summaryLine}</div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
            <div className="rounded-md border border-white/5 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Trailing dues</div>
              <div className="tabular-nums text-white">{money(trailingDues)}</div>
            </div>
            <div className="rounded-md border border-white/5 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Trailing expenses</div>
              <div className="tabular-nums text-white">{money(data.annual_expenses)}</div>
            </div>
            <div className="rounded-md border border-white/5 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Starting reserve</div>
              <div className="tabular-nums text-white">{money(startBalance)}</div>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

export default ReserveForecastCard;
